<template>
  <DxForm id="formularioServicioEnergia" ref="formInstance" :form-data="model" label-location="top" :show-colon-after-label="false" :visible="model != null"
    validation-group="validacionServicioEnergia">
    <DxColCountByScreen :xs="6" :sm="6" :md="6" :lg="6" />

    <DxGroupItem :col-span="6" :col-count="6" :visible="mostrarSelectorModo">
      <DxSimpleItem :col-span="6">
        <DxRadioGroup
          :items="modoAnexoItems"
          :value="modoAnexo"
          layout="horizontal"
          display-expr="text"
          value-expr="value"
          :read-only="tieneTramitesAsociados"
          @value-changed="onModoAnexoChanged"
        />
        <DxLabel :visible="false" />
      </DxSimpleItem>
    </DxGroupItem>

    <!-- Sección PDF -->
    <DxGroupItem :col-span="6" :col-count="6" :visible="modoAnexoEfectivo === 'pdf'">
      <DxSimpleItem :col-span="2">
        <DocumentCard ref="documentCard" :title="$t('servicio_energia.form.titulo_texto_anexo')" :initial-document-list="documentoAsociado"
          :required="true" layout="default" :max-files="1" :multiple="false"
          :actions="props.tieneTramitesAsociados ? ['open', 'download'] : ['open', 'replace', 'download', 'add']" @updated:document="seleccionarDocumento" @added:document="seleccionarDocumento" />
      </DxSimpleItem>
      <DxSimpleItem :col-span="4">
        <coordenadas-firma v-model:model="props.model.CoordenadasFirma" :read-only="$props.tieneTramitesAsociados" ref="coordenadasFirma" />
      </DxSimpleItem>
    </DxGroupItem>

    <!-- Sección Plantilla HTML -->
    <DxGroupItem :col-span="6" :col-count="6" :visible="modoAnexoEfectivo === 'plantilla'">
      <DxSimpleItem :col-span="3">
        <DxLabel :text="$t('servicio_energia.form.plantilla_html.selector_anexo')" />
        <DxSelectBox
          :data-source="anexosDataSource"
          display-expr="Nombre"
          value-expr="Id"
          :value="selectedAnexoId"
          :read-only="tieneTramitesAsociados"
          :search-enabled="true"
          :show-clear-button="!tieneTramitesAsociados"
          @value-changed="onAnexoSeleccionado"
        />
      </DxSimpleItem>
      <DxEmptyItem :col-span="3" />

      <DxSimpleItem :col-span="6" :visible="definicionVariables.length > 0">
        <DxLabel :text="$t('servicio_energia.form.plantilla_html.titulo_variables')" />
        <div class="variables-container">
          <div v-for="variable in definicionVariables" :key="variable.Codigo" class="variable-item">
            <template v-if="variable.Tipo === 'BOOL'">
              <DxCheckBox
                :text="variable.Codigo"
                :value="getVariableBoolValue(variable.Codigo)"
                :read-only="tieneTramitesAsociados"
                @value-changed="(e: any) => setVariableBoolValue(variable.Codigo, e.value)"
              />
            </template>
            <template v-else>
              <DxTextBox
                :label="variable.Codigo"
                label-mode="floating"
                :value="getVariableStringValue(variable.Codigo)"
                :read-only="tieneTramitesAsociados"
                @value-changed="(e: any) => setVariableStringValue(variable.Codigo, e.value)"
              />
            </template>
          </div>
        </div>
      </DxSimpleItem>
      <DxSimpleItem :col-span="6">
        <coordenadas-firma v-model:model="props.model.CoordenadasFirma" :read-only="$props.tieneTramitesAsociados" ref="coordenadasFirma" />
      </DxSimpleItem>
    </DxGroupItem>
  </DxForm>
</template>

<script setup lang="ts">
import {
  DxForm,
  DxLabel,
  DxColCountByScreen,
  DxEmptyItem,
  DxSimpleItem,
  DxGroupItem
} from "devextreme-vue/form";
import { DxCheckBox, DxRadioGroup, DxSelectBox, DxTextBox } from "devextreme-vue";
import type { CreateServicioEndesaModel, DetailServicioEndesaModel, DefinicionVariableAnexoDto, VariableAnexoValor } from "@api/api";
import DocumentCard from "@/.shared/components/document-card/DocumentCard.vue";
import CoordenadasFirma from "@/components/coordenadas-firma/CoordenadasFirma.vue";
import { ref, computed, onMounted } from "vue";
import type { CardFile } from "@/.shared/components/document-card/models/CardFile";
import { uniqueId } from "lodash";
import type { IDocumentInitialState } from "@/.shared/components/document-card/models/IDocumentCardProps";
import { useApi } from "@/.shared/.api/integration";
import { useIntl } from "@/.shared/services/i18n/i18nservice";
import { Constants } from "@/.shared/constants/OperacionEnergia";

const api = useApi();
const intl = useIntl();

const props = defineProps({
  model: {
    type: Object as () => DetailServicioEndesaModel | CreateServicioEndesaModel,
    required: true
  },
  tieneTramitesAsociados: {
    type: Boolean,
    required: true
  }
});

type ModoAnexo = "pdf" | "plantilla";

function empresaConAnexoPlantilla(idEmpresaServicios: string | null | undefined): boolean {
  return !!idEmpresaServicios &&
    Constants.EmpresaServicios.EMPRESAS_CON_ANEXO_PLANTILLA.includes(idEmpresaServicios);
}

function resolverModoInicial(model: DetailServicioEndesaModel | CreateServicioEndesaModel): ModoAnexo {
  if (!empresaConAnexoPlantilla(model.IdEmpresaServicios)) {
    return "pdf";
  }

  if (model.IdAnexoEmpresaServicios) {
    return "plantilla";
  }

  if ((model as DetailServicioEndesaModel).Anexo) {
    return "pdf";
  }

  return "plantilla";
}

const documentoASubir = ref<File>();
const documentCard = ref<InstanceType<typeof DocumentCard>>();
const coordenadasFirma = ref<InstanceType<typeof CoordenadasFirma>>();

const modoAnexo = ref<ModoAnexo>(resolverModoInicial(props.model));
const selectedAnexoId = ref<string | null>(null);
const definicionVariables = ref<DefinicionVariableAnexoDto[]>([]);
const variablesValor = ref<VariableAnexoValor[]>([]);
const anexosDataSource = ref<any[]>([]);

const modoAnexoItems = computed(() => [
  { value: "pdf", text: intl.i18n.t("servicio_energia.form.modo_anexo.pdf") },
  { value: "plantilla", text: intl.i18n.t("servicio_energia.form.modo_anexo.plantilla_html") }
]);

const mostrarSelectorModo = computed(() =>
  empresaConAnexoPlantilla(props.model.IdEmpresaServicios)
);

const modoAnexoEfectivo = computed<ModoAnexo>(() => {
  if (!mostrarSelectorModo.value) {
    return "pdf";
  }
  return modoAnexo.value;
});

const documentoAsociado = computed<IDocumentInitialState[]>(() => {
  const remoteUri = (props.model as DetailServicioEndesaModel).Anexo ?? null;

  if (!remoteUri) {
    return [];
  }

  return [{
    id: uniqueId(),
    uri: remoteUri,
    fileName: remoteUri.split("/").pop() ?? "",
    description: undefined
  }];
});

onMounted(async () => {
  if (!mostrarSelectorModo.value) {
    return;
  }

  await cargarAnexosDisponibles();

  if (props.model.IdAnexoEmpresaServicios && modoAnexoEfectivo.value === "plantilla") {
    await aplicarAnexo(props.model.IdAnexoEmpresaServicios, props.model.VariablesAnexo);
  }
});

async function cargarAnexosDisponibles() {
  const idEmpresaServicios = props.model.IdEmpresaServicios;
  if (!idEmpresaServicios) return;

  const result = await api.callStandardApi(
    "getAnexosEmpresaServiciosForGrid",
    { showLoading: false },
    idEmpresaServicios,
    {}
  );
  anexosDataSource.value = (result?.data ?? result) as any[];
}

async function cargarDefinicionVariables(idAnexo: string) {
  const result = await api.callStandardApi(
    "getAnexoEmpresaServiciosById",
    { showLoading: false },
    idAnexo
  );
  definicionVariables.value = result?.DefinicionVariables ?? [];
}

async function aplicarAnexo(idAnexo: string, variablesExistentes?: VariableAnexoValor[] | null) {
  selectedAnexoId.value = idAnexo;
  await cargarDefinicionVariables(idAnexo);
  variablesValor.value = variablesExistentes?.length
    ? [...variablesExistentes]
    : crearVariablesPorDefecto();
}

function limpiarAnexo() {
  selectedAnexoId.value = null;
  definicionVariables.value = [];
  variablesValor.value = [];
}

function crearVariablesPorDefecto(): VariableAnexoValor[] {
  return definicionVariables.value.map(def => ({
    Codigo: def.Codigo,
    ValorBool: def.Tipo === "BOOL" ? false : null,
    ValorString: def.Tipo === "STRING" ? "" : null
  }));
}

function onModoAnexoChanged(e: { value: ModoAnexo; event?: Event }) {
  if (!e.event) {
    return;
  }

  modoAnexo.value = e.value;
  if (e.value === "pdf") {
    limpiarAnexo();
  }
}

async function onAnexoSeleccionado(e: { value: string | null; event?: Event }) {
  if (!e.event) {
    return;
  }

  if (!e.value) {
    limpiarAnexo();
    return;
  }

  await aplicarAnexo(e.value);
}

function getVariableBoolValue(codigo: string): boolean {
  return variablesValor.value.find(v => v.Codigo === codigo)?.ValorBool ?? false;
}

function setVariableBoolValue(codigo: string, value: boolean) {
  const variable = variablesValor.value.find(v => v.Codigo === codigo);
  if (variable) variable.ValorBool = value;
}

function getVariableStringValue(codigo: string): string {
  return variablesValor.value.find(v => v.Codigo === codigo)?.ValorString ?? "";
}

function setVariableStringValue(codigo: string, value: string) {
  const variable = variablesValor.value.find(v => v.Codigo === codigo);
  if (variable) variable.ValorString = value;
}

const seleccionarDocumento = (ficheroSeleccionado: CardFile) => {
  documentoASubir.value = ficheroSeleccionado.file ?? undefined;
};

function isPlantillaMode(): boolean {
  return modoAnexoEfectivo.value === "plantilla";
}

async function validate(): Promise<boolean> {
  if (isPlantillaMode()) {
    return selectedAnexoId.value != null;
  }
  return documentCard.value?.isValid ?? false;
}

function getAnexoData(): { idAnexo: string | null; variables: VariableAnexoValor[] | null } {
  if (isPlantillaMode() && selectedAnexoId.value) {
    return {
      idAnexo: selectedAnexoId.value,
      variables: variablesValor.value
    };
  }
  return { idAnexo: null, variables: null };
}

defineExpose({ documentoASubir, validate, isPlantillaMode, getAnexoData });

</script>

<style lang="scss" scoped>
.variables-container {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  padding: 8px 0;

  .variable-item {
    min-width: 250px;
    flex: 1 1 calc(33% - 16px);
  }
}
</style>
